import type { Address } from '@logistiek-zonder-papier/lzp-library'
import type {
  AddressBookLocation,
  AddressBookOrganization,
} from '@logistiek-zonder-papier/lzp-library'

export type CreateAddress = Omit<Address, 'id' | 'customerId'>

export function createEmptyAddress(): CreateAddress {
  return {
    street: '',
    street2: null,
    houseNumber: '',
    suffix: null,
    postalCode: '',
    city: '',
    country: 'NL',
    longitude: null,
    latitude: null,
  }
}

// Empty strings in the form become null, because the backend rejects ''
export function normalizeAddress(address: CreateAddress): CreateAddress {
  return {
    ...address,
    suffix: address.suffix || null,
    street2: address.street2 || null,
  }
}

export type CreateAddressBookLocation = Omit<
  AddressBookLocation,
  'id' | 'customerId' | 'organizationId' | 'addressId' | 'address'
> & {
  address?: CreateAddress
}

export type CreateAddressBookOrganization = Omit<
  AddressBookOrganization,
  'id' | 'customerId' | 'address' | 'locations'
> & {
  address: CreateAddress
  locations: CreateAddressBookLocation[]
}

export function createEmptyAddressBookOrganization(): CreateAddressBookOrganization {
  return {
    name: '',
    vihbNumber: null,
    address: createEmptyAddress(),
    types: [],
    locations: [],
  }
}

export function createEmptyAddressBookLocation(): CreateAddressBookLocation {
  return { types: [], locationNumber: null }
}

export function normalizeLocation(location: CreateAddressBookLocation): CreateAddressBookLocation {
  const { address, ...rest } = location
  return address ? { ...rest, address: normalizeAddress(address) } : rest
}

export function normalizeEditedLocation(
  location: AddressBookLocation
): Partial<AddressBookLocation> {
  return {
    locationNumber: location.locationNumber,
    types: location.types,
    id: location.id,
    organizationId: location.organizationId,
    ...(location.address && {
      address: { ...location.address, ...normalizeAddress(location.address) },
    }),
  }
}

export function normalizeOrganization(
  org: CreateAddressBookOrganization
): CreateAddressBookOrganization {
  return {
    ...org,
    address: normalizeAddress(org.address),
    locations: org.locations.map(normalizeLocation),
  }
}
